/**
 * ProductDetailV2.jsx
 * 
 * Unified product detail page for V2 template-based products.
 * Handles all product types (LGF, SMF, AWD, PROMO) with their
 * respective pricing models (per_sqft, quantity_tier, base_plus_options).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Loader2, 
  Package, 
  Clock, 
  Shield, 
  Truck,
  CheckCircle2,
  Info,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { productsV2API, pricingV2API } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  if (imageUrl.startsWith('/')) {
    return `${API_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Product type labels
const PRODUCT_TYPE_LABELS = {
  LGF: 'Large Format',
  SMF: 'Small Format',
  AWD: 'Awards',
  PROMO: 'Promotional'
};

// Back links by product type
const BACK_LINKS = {
  LGF: { path: '/LargePrint/products', label: 'Back to Large Format' },
  SMF: { path: '/SmallPrint/products', label: 'Back to Small Format' },
  AWD: { path: '/Awards/products', label: 'Back to Awards' },
  PROMO: { path: '/Promo/products', label: 'Back to Promotional' }
};

const ProductDetailV2 = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  
  // Product & template state
  const [product, setProduct] = useState(null);
  const [template, setTemplate] = useState(null);
  const [optionSets, setOptionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Configuration state
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 24, height: 36 });
  
  // Pricing state
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  
  // Gallery state
  const [selectedImage, setSelectedImage] = useState(0);

  // Fetch product and related data
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch product
        const productData = await productsV2API.getProduct(productId);
        setProduct(productData);
        
        // Fetch template if product has one
        if (productData.template_id) {
          const templatesRes = await productsV2API.getTemplates();
          const productTemplate = templatesRes.templates?.find(
            t => t.id === productData.template_id
          );
          if (productTemplate) {
            setTemplate(productTemplate);
            
            // Fetch option sets for this template
            if (productTemplate.option_set_ids?.length > 0) {
              const optionSetsRes = await productsV2API.getOptionSets();
              const relevantSets = optionSetsRes.option_sets?.filter(
                os => productTemplate.option_set_ids.includes(os.id)
              ) || [];
              setOptionSets(relevantSets);
              
              // Initialize selected options with defaults
              const initialOptions = {};
              relevantSets.forEach(os => {
                const defaultChoice = os.choices?.find(c => c.is_default) || os.choices?.[0];
                if (defaultChoice) {
                  // Use 'id' field as value, store with option set 'code'
                  initialOptions[os.code] = defaultChoice.id;
                }
              });
              setSelectedOptions(initialOptions);
            }
          }
        }
        
        // Set initial quantity based on template
        if (productData.min_quantity) {
          setQuantity(productData.min_quantity);
        }
        
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [productId]);

  // Calculate price when configuration changes
  const calculatePrice = useCallback(async () => {
    if (!product || !template) return;
    
    setPriceLoading(true);
    try {
      // Get pricing model from template.pricing.model
      const pricingModel = template.pricing?.model || 'base_plus_options';
      
      // Convert options from code-based to id-based for backend API
      // Frontend stores: { MATERIAL: 'ch_18oz' } (code -> choice_id)
      // Backend expects: { opts_banner_material: 'ch_18oz' } (option_set_id -> choice_id)
      const optionsById = {};
      Object.entries(selectedOptions).forEach(([code, choiceId]) => {
        const optSet = optionSets.find(os => os.code === code);
        if (optSet) {
          optionsById[optSet.id] = choiceId;
        }
      });
      
      // Build configuration based on pricing model
      const configuration = {
        quantity,
        options: optionsById
      };
      
      // Add dimensions for per_sqft products
      if (pricingModel === 'per_sqft') {
        configuration.width = dimensions.width;
        configuration.height = dimensions.height;
        configuration.double_sided = selectedOptions.SIDES === 'ch_double';
      }
      
      const result = await pricingV2API.calculate({
        product_id: product.id,
        configuration
      });
      setCalculatedPrice(result);
      
    } catch (err) {
      console.error('Error calculating price:', err);
      // Fallback to simple calculation
      const basePrice = product.base_cost || template.pricing?.base_price_per_sqft || 10;
      setCalculatedPrice({
        retail_price: basePrice * quantity,
        unit_price: basePrice,
        quantity
      });
    } finally {
      setPriceLoading(false);
    }
  }, [product, template, quantity, selectedOptions, dimensions, optionSets]);

  // Recalculate price when config changes
  useEffect(() => {
    if (product && template) {
      calculatePrice();
    }
  }, [product, template, quantity, selectedOptions, dimensions, calculatePrice]);

  // Handle option change
  const handleOptionChange = (optionCode, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionCode]: value
    }));
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!product || !calculatedPrice) return;

    // Get the total price from calculation
    const totalPrice = calculatedPrice.total || calculatedPrice.retail_price || 0;
    const unitPrice = calculatedPrice.unit_price || totalPrice / quantity;

    const cartProduct = {
      id: product.id,
      name: product.name,
      image: getImageUrl(product.images?.primary),
      sku: product.sku,
      basePrice: unitPrice,
      section: product.product_type?.toLowerCase() === 'lgf' ? 'largeformat' : 
               product.product_type?.toLowerCase() === 'smf' ? 'smallformat' :
               product.product_type?.toLowerCase() === 'awd' ? 'awards' : 'general',
      product_type: product.product_type
    };

    // Build options object with human-readable values
    const options = {};
    
    // Convert option IDs to human-readable names
    Object.entries(selectedOptions).forEach(([code, choiceId]) => {
      const optSet = optionSets.find(os => os.code === code);
      if (optSet) {
        const choice = optSet.choices?.find(c => c.id === choiceId);
        options[optSet.name] = choice?.name || choiceId;
      }
    });
    
    // Add dimensions for per_sqft products
    const pricingModel = template?.pricing?.model;
    if (pricingModel === 'per_sqft') {
      options.Width = `${dimensions.width}"`;
      options.Height = `${dimensions.height}"`;
      options['Square Feet'] = ((dimensions.width / 12) * (dimensions.height / 12)).toFixed(2);
    }

    addToCart(cartProduct, options, quantity);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
      duration: 3000,
    });
  };

  // Handle buy now
  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  // Get gallery images
  const galleryImages = useMemo(() => {
    if (!product?.images) return [];
    const images = [];
    if (product.images.primary) images.push(getImageUrl(product.images.primary));
    if (product.images.gallery) {
      product.images.gallery.forEach(img => {
        if (img) images.push(getImageUrl(img));
      });
    }
    return images;
  }, [product]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading product...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Card className="bg-ivory-100 border border-ivory-300/50">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-ivory-300 mx-auto mb-4" />
            <p className="text-ivory-600 text-lg mb-4 font-body">Product not found</p>
            <Button className="btn-luxury-primary" asChild>
              <Link to="/">Browse Products</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const backLink = BACK_LINKS[product.product_type] || { path: '/', label: 'Back to Products' };
  const productTypeLabel = PRODUCT_TYPE_LABELS[product.product_type] || 'Product';

  const guaranteeFeatures = [
    { icon: Package, text: 'Premium quality' },
    { icon: Clock, text: 'Fast turnaround' },
    { icon: Shield, text: 'Satisfaction guaranteed' },
    { icon: Truck, text: 'Fast shipping' },
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link 
          to={backLink.path}
          className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLink.label}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          {/* Left Column - Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <Card className="bg-ivory-100 border border-ivory-300/50 overflow-hidden">
              <div className="aspect-square bg-white">
                {galleryImages.length > 0 ? (
                  <img
                    src={galleryImages[selectedImage] || galleryImages[0]}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Product';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-ivory-400" />
                  </div>
                )}
              </div>
            </Card>
            
            {/* Gallery Thumbnails */}
            {galleryImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-colors ${
                      selectedImage === idx ? 'border-gold-500' : 'border-ivory-300/50 hover:border-gold-300'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Product Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Product Info & Config */}
          <div className="space-y-6">
            {/* Product Title & Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-xs">
                  {productTypeLabel}
                </Badge>
                {product.status === 'active' && (
                  <Badge className="bg-green-100 text-green-700 text-xs">In Stock</Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-2" data-testid="product-name">
                {product.name}
              </h1>
              {product.sku && (
                <p className="text-sm text-ivory-500 font-mono">SKU: {product.sku}</p>
              )}
            </div>

            {/* Description */}
            {product.descriptions?.short && (
              <p className="text-lg text-ivory-600 font-body leading-relaxed">
                {product.descriptions.short}
              </p>
            )}

            {/* Price Display */}
            <div className="p-5 rounded-xl bg-gold-50 border border-gold-200">
              {priceLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-gold-600" />
                  <span className="text-ivory-600">Calculating...</span>
                </div>
              ) : calculatedPrice ? (
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-heading font-bold text-gold-600" data-testid="product-price">
                      ${(calculatedPrice.total || calculatedPrice.retail_price || 0).toFixed(2)}
                    </span>
                    {template?.pricing?.model === 'per_sqft' && (
                      <span className="text-lg text-ivory-600 font-body">
                        ({calculatedPrice.unit_price?.toFixed(2)}/sq ft)
                      </span>
                    )}
                    {template?.pricing?.model === 'quantity_tier' && quantity > 1 && (
                      <span className="text-lg text-ivory-600 font-body">
                        (${calculatedPrice.unit_price?.toFixed(2)} each)
                      </span>
                    )}
                  </div>
                  {calculatedPrice.applied_tier && (
                    <p className="text-sm text-green-600 mt-1 font-body">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      {calculatedPrice.applied_tier} pricing applied
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-2xl font-heading font-bold text-ivory-400">
                  Configure options to see price
                </span>
              )}
            </div>

            {/* Configuration Card */}
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-5 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-gold-600" />
                  Configure Your Order
                </h3>
                
                <div className="space-y-5">
                  {/* Dimensions for per_sqft products */}
                  {template?.pricing?.model === 'per_sqft' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="width" className="text-sm font-medium text-ivory-600 mb-2 block font-body">
                            Width (inches)
                          </Label>
                          <Input
                            id="width"
                            type="number"
                            value={dimensions.width}
                            onChange={(e) => setDimensions(prev => ({ ...prev, width: parseFloat(e.target.value) || 0 }))}
                            className="bg-white border-ivory-300 text-ivory-800"
                            min="1"
                            data-testid="width-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="height" className="text-sm font-medium text-ivory-600 mb-2 block font-body">
                            Height (inches)
                          </Label>
                          <Input
                            id="height"
                            type="number"
                            value={dimensions.height}
                            onChange={(e) => setDimensions(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                            className="bg-white border-ivory-300 text-ivory-800"
                            min="1"
                            data-testid="height-input"
                          />
                        </div>
                      </div>
                      
                      {/* Size Summary */}
                      <div className="bg-gold-500/10 p-4 rounded-lg">
                        <div className="flex justify-between items-center text-sm font-body">
                          <span className="text-ivory-600">
                            {dimensions.width}" x {dimensions.height}" = {((dimensions.width / 12) * (dimensions.height / 12)).toFixed(2)} sq ft
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Dynamic Options from Option Sets */}
                  {optionSets.map((optionSet) => (
                    <div key={optionSet.id}>
                      <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">
                        {optionSet.name}
                        {optionSet.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Select 
                        value={selectedOptions[optionSet.code] || ''} 
                        onValueChange={(value) => handleOptionChange(optionSet.code, value)}
                      >
                        <SelectTrigger 
                          className="bg-white border-ivory-300 text-ivory-800"
                          data-testid={`option-${optionSet.code}`}
                        >
                          <SelectValue placeholder={`Select ${optionSet.name.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-ivory-300">
                          {optionSet.choices?.filter(c => c.is_active !== false).map((choice) => (
                            <SelectItem 
                              key={choice.id} 
                              value={choice.id} 
                              className="text-ivory-800"
                            >
                              {choice.name}
                              {choice.price_modifier?.value > 0 && (
                                <span className="text-ivory-500 ml-2">
                                  (+{choice.price_modifier.type === 'percent' ? `${choice.price_modifier.value}%` : `$${choice.price_modifier.value.toFixed(2)}`})
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {optionSet.description && (
                        <p className="text-xs text-ivory-500 mt-1">{optionSet.description}</p>
                      )}
                    </div>
                  ))}

                  {/* Quantity */}
                  <div>
                    <Label htmlFor="quantity" className="text-sm font-medium text-ivory-600 mb-2 block font-body">
                      Quantity
                    </Label>
                    {template?.pricing?.model === 'quantity_tier' && template?.pricing?.quantity_tiers?.length > 0 ? (
                      <Select 
                        value={quantity.toString()} 
                        onValueChange={(value) => setQuantity(parseInt(value))}
                      >
                        <SelectTrigger className="bg-white border-ivory-300 text-ivory-800" data-testid="quantity-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-ivory-300">
                          {template.quantity_tiers.map((tier) => (
                            <SelectItem 
                              key={tier.min_qty} 
                              value={tier.min_qty.toString()} 
                              className="text-ivory-800"
                            >
                              {tier.min_qty.toLocaleString()} {tier.label && `- ${tier.label}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="quantity"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="bg-white border-ivory-300 text-ivory-800 w-32"
                        min="1"
                        data-testid="quantity-input"
                      />
                    )}
                    {product.min_quantity && product.min_quantity > 1 && (
                      <p className="text-xs text-ivory-500 mt-1">
                        Minimum order: {product.min_quantity} units
                      </p>
                    )}
                  </div>
                </div>

                {/* Total */}
                {calculatedPrice && (
                  <div className="mt-6 pt-4 border-t border-ivory-200">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-body text-ivory-600">Total</span>
                      <span className="text-2xl font-heading font-bold text-gold-600">
                        ${(calculatedPrice.total || calculatedPrice.retail_price || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full text-lg py-6 btn-luxury-primary rounded-xl font-body"
                onClick={handleBuyNow}
                disabled={!calculatedPrice || priceLoading}
                data-testid="buy-now-btn"
              >
                {priceLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  `Buy Now - $${(calculatedPrice?.total || calculatedPrice?.retail_price || 0).toFixed(2)}`
                )}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6 btn-luxury-outline rounded-xl font-body"
                onClick={handleAddToCart}
                disabled={!calculatedPrice || priceLoading}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Guarantee Features */}
            <div className="grid grid-cols-2 gap-3">
              {guaranteeFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 p-3 rounded-lg bg-ivory-100 border border-ivory-300/50"
                >
                  <feature.icon className="w-4 h-4 text-gold-600 flex-shrink-0" />
                  <span className="text-xs text-ivory-600 font-body">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Description Section */}
        {product.descriptions?.full && (
          <div className="max-w-7xl mx-auto mt-12">
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Info className="w-5 h-5 text-gold-600" />
                  <h3 className="text-xl font-heading font-semibold text-ivory-800">Product Details</h3>
                </div>
                <div className="prose prose-ivory max-w-none font-body text-ivory-600 leading-relaxed">
                  {product.descriptions.full}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailV2;
